import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserProgramPreferencesService } from '../services/userProgramPreferencesService';
import { useDialog } from '../context/DialogContext';
import styles from '../styles/onboardingWizard.module.css';
import { useLocale } from '../context/LocaleContext';

interface StepProps {
  title: string;
  children: React.ReactNode;
  note?: string;
}

const Step: React.FC<StepProps> = ({ title, children, note }) => (
  <div className={styles.step}>
    <h2>{title}</h2>
    {children}
    {note && <p className={styles.note}>{note}</p>}
  </div>
);

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useDialog();
  const { t } = useLocale();
  const [primaryProgram, setPrimaryProgram] = useState('');
  
  // Get preselected programs from assessment results
  const preselectedPrograms: string[] = location.state?.preselectedPrograms || [];
  const fromAssessment: boolean = location.state?.fromAssessment || false;

  // Auto-select primary program if coming from assessment
  useEffect(() => {
    if (fromAssessment && preselectedPrograms.length > 0) {
      const program = preselectedPrograms[0];
      setPrimaryProgram(program);
    }
  }, [fromAssessment, preselectedPrograms]);

  const handlePrimaryProgramSelect = async (program: string) => {
    setPrimaryProgram(program);
    
    // Auto-proceed after selection
    try {
      // Save user preferences to database
      if (program !== "Not sure yet") {
        const programLower = program.toLowerCase();
        
        // Get existing preferences to avoid overwriting
        let existingPreferences;
        try {
          existingPreferences = await UserProgramPreferencesService.getUserProgramPreferences();
        } catch (error) {
          // No existing preferences, that's okay
          existingPreferences = null;
        }

        const existingPrograms = existingPreferences?.selectedPrograms || [];
        const existingPrimary = existingPreferences?.primaryProgram;

        // Add new program to list if not already there
        const updatedPrograms = existingPrograms.includes(programLower) 
          ? existingPrograms 
          : [...existingPrograms, programLower];

        // Only set as primary if user has no existing primary program
        const finalPrimary = existingPrimary || programLower;

        await UserProgramPreferencesService.saveUserProgramPreferences({
          selectedPrograms: updatedPrograms,
          primaryProgram: finalPrimary
        });
      }
      
      // Set localStorage flag
      localStorage.setItem("rc_has_account", "true");
      
      // Navigate to home page immediately
      navigate('/');
    } catch (error) {
      // Show user-friendly error message
      const errorMessage = error instanceof Error 
        ? error.message 
        : t('onboarding.save_failed');
      
      showAlert({
        title: t('onboarding.notice'),
        message: errorMessage,
        okText: t('onboarding.ok')
      });
      
      // Still navigate even if save fails - don't block user progress
      navigate('/');
    }
  };


  const handleSkip = () => {
    localStorage.setItem("rc_has_account", "true");
    navigate('/');
  };

  // Program display names with full names
  const programOptions = [
    { value: "AA", display: t('onboarding.program.aa') },
    { value: "SAA", display: t('onboarding.program.saa') },
    { value: "NA", display: t('onboarding.program.na') },
    { value: "OA", display: t('onboarding.program.oa') },
    { value: "GA", display: t('onboarding.program.ga') },
    { value: "DA", display: t('onboarding.program.da') },
    { value: "CoDA", display: t('onboarding.program.coda') },
    { value: "CA", display: t('onboarding.program.ca') },
    { value: "Not sure yet", display: t('onboarding.program.not_sure') }
  ];

  const renderContent = () => {
    return (
      <Step
        title={fromAssessment ? t('onboarding.assessment_title') : t('onboarding.title')}
        note={fromAssessment
          ? preselectedPrograms[0]
            ? t('onboarding.assessment_note', { program: preselectedPrograms[0] })
            : t('onboarding.assessment_note_generic')
          : t('onboarding.note')
        }
      >
        {fromAssessment && preselectedPrograms.length > 0 && (
          <div className={styles.assessmentResults}>
            <h3>{t('onboarding.recommended_programs')}</h3>
            <div className={styles.recommendedPrograms}>
              {preselectedPrograms.map((program, index) => {
                const programOption = programOptions.find(p => p.value.toLowerCase() === program.toLowerCase());
                return (
                  <div key={program} className={styles.recommendedProgram}>
                    <span className={styles.programRank}>#{index + 1}</span>
                    <span className={styles.programName}>{programOption?.display || program.toUpperCase()}</span>
                    {index === 0 && <span className={styles.primaryLabel}>{t('onboarding.primary')}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        <div className={styles.options}>
          {programOptions.map((program) => (
            <button
              key={program.value}
              onClick={() => handlePrimaryProgramSelect(program.value)}
              className={`${styles.optionButton} ${
                primaryProgram === program.value ? styles.selected : ''
              } ${
                preselectedPrograms.includes(program.value.toLowerCase()) ? styles.recommended : ''
              }`}
            >
              {program.display}
              {preselectedPrograms.includes(program.value.toLowerCase()) && (
                <span className={styles.recommendedBadge}>{t('onboarding.recommended')}</span>
              )}
            </button>
          ))}
        </div>

      </Step>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {renderContent()}
      </div>

      <div className={styles.skipSection}>
        <button onClick={handleSkip} className={styles.skipButton}>
          {t('onboarding.skip')}
        </button>
      </div>
    </div>
  );
}
